import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Step, CallBackProps } from 'react-joyride';
import { removeAuthCookie } from '../utils/authUtils';

export interface TutorialStep extends Step {
  target: string;
  content: string;
  title?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center' | 'auto';
  disableBeacon?: boolean;
  hideCloseButton?: boolean;
  hideFooter?: boolean;
  showProgress?: boolean;
  showSkipButton?: boolean;
  spotlightClicks?: boolean;
  disableScrolling?: boolean;
  disableOverlayClose?: boolean;
  styles?: any;
}

const filterExistingSteps = (steps: TutorialStep[]): TutorialStep[] =>
  steps.filter((step) => document.querySelector(step.target) !== null);

/** Signature stable : évite une nouvelle ref Joyride quand le parent recrée le même tableau. */
const getStepsSignature = (steps: TutorialStep[]): string =>
  steps
    .map((s) => `${s.target}\0${s.title ?? ''}\0${s.content ?? ''}\0${s.placement ?? ''}`)
    .join('\n');

export const useTutorial = (
  steps: TutorialStep[],
  tutorialKey: string,
  autoStart: boolean = false,
  disableFiltering: boolean = false,
) => {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const stepsSignature = getStepsSignature(steps);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stabilisé via stepsSignature
  const stableSteps = useMemo(() => steps, [stepsSignature]);

  const stepsRef = useRef(stableSteps);
  stepsRef.current = stableSteps;

  const runRef = useRef(run);
  runRef.current = run;

  // Re-filtrer quand le tutoriel démarre : les cibles DOM (data-tutorial) peuvent
  // apparaître après le 1er render (TutorialDataAttributes ~1s, auto-start ~2s).
  const validSteps = useMemo(() => {
    if (disableFiltering) return stableSteps;
    return filterExistingSteps(stableSteps);
  }, [stableSteps, disableFiltering, run]);

  const startTutorial = useCallback(() => {
    if (runRef.current) {
      return;
    }

    const currentSteps = stepsRef.current;
    const stepsToUse = disableFiltering ? currentSteps : filterExistingSteps(currentSteps);

    if (stepsToUse.length === 0) {
      console.warn('⚠️ useTutorial - Aucun élément cible trouvé, tutoriel non démarré');
      return;
    }

    setRun(true);
    setStepIndex(0);
  }, [disableFiltering]);

  const stopTutorial = useCallback(() => {
    setRun(false);
    setStepIndex(0);
  }, []);

  const resetTutorial = useCallback(() => {
    setStepIndex(0);
    setRun(true);
  }, []);

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { action, index, status, type } = data;

      const statusStr = status?.toString() || '';
      if (statusStr.includes('error') && statusStr.includes('target')) {
        console.warn('⚠️ useTutorial - Élément cible non trouvé, on continue quand même');
        return;
      }

      if (['finished', 'skipped'].includes(status)) {
        setRun(false);
        setStepIndex(0);
        localStorage.setItem(`tutorial_${tutorialKey}_completed`, 'true');
        localStorage.setItem('famla_first_login_done', 'true');
        sessionStorage.removeItem('api_first_login');
        removeAuthCookie('is_first_login');
      } else if (type === 'step:after') {
        setStepIndex(index + (action === 'prev' ? -1 : 1));
      }
    },
    [tutorialKey],
  );

  const isTutorialCompleted = useCallback(() => {
    return localStorage.getItem(`tutorial_${tutorialKey}_completed`) === 'true';
  }, [tutorialKey]);

  const markTutorialAsCompleted = useCallback(() => {
    localStorage.setItem(`tutorial_${tutorialKey}_completed`, 'true');
  }, [tutorialKey]);

  const resetTutorialProgress = useCallback(() => {
    localStorage.removeItem(`tutorial_${tutorialKey}_completed`);
  }, [tutorialKey]);

  // Auto-start uniquement si is_first_login=true (deps stables → pas de boucle render).
  useEffect(() => {
    if (!autoStart || run) return;
    if (localStorage.getItem(`tutorial_${tutorialKey}_completed`) === 'true') return;

    const apiFirstLogin = sessionStorage.getItem('api_first_login');
    const cookieFirstLogin = document.cookie
      .split('; ')
      .find((row) => row.startsWith('is_first_login='))
      ?.split('=')[1];
    const isFirstLogin = apiFirstLogin === 'true' || cookieFirstLogin === 'true';

    if (!isFirstLogin) return;

    const timer = setTimeout(() => {
      startTutorial();
    }, 2000);

    return () => clearTimeout(timer);
  }, [autoStart, tutorialKey, run, startTutorial]);

  return {
    run,
    stepIndex,
    steps: validSteps,
    startTutorial,
    stopTutorial,
    resetTutorial,
    handleJoyrideCallback,
    isTutorialCompleted,
    markTutorialAsCompleted,
    resetTutorialProgress,
  };
};
